<script setup lang="ts">
import AlertComponent from "@/components/AlertComponent.vue";
import BreadCrumb, { Page } from "@/components/BreadCrumb.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import ModalComponent from "@/components/ModalComponent.vue";
import ShadowBox from "@/components/ShadowBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledInput from "@/components/StyledInput.vue";
import useContactTypes from "@/internal/composables/useContactTypes";
import { useQueryClient } from "@tanstack/vue-query";
import { reactive, ref } from "vue";
import { useToast } from "vue-toastification";
import ProviderList from "../../components/ProviderList.vue";

const {
  contactTypes,
  isLoading,
  deleteContactTypeAsync,
  createContactTypeAsync,
  createContactTypeLoading,
} = useContactTypes({
  immediate: true,
});

const toast = useToast();
const queryClient = useQueryClient();

const pages = ref<Page[]>([
  {
    name: "Tipo de contactos",
    to: {
      name: "contact-type",
    },
  },
]);

const actionModalDelete = ref(false);
const actionModalCreate = ref(false);

const contact = reactive({
  id: 0,
  name: "",
});

const actionCreate = () => {
  actionModalCreate.value = !actionModalCreate.value;
};

const handleCreate = async () => {
  if (!contact.name) {
    toast.error("El nombre del contacto es requerido");
    return;
  }

  await createContactTypeAsync({
    name: contact.name,
  });

  actionCreate();
  contact.name = "";
  await queryClient.invalidateQueries(["contact-types"]);
};

const actionDelete = () => {
  actionModalDelete.value = !actionModalDelete.value;
};

const handleDelete = async () => {
  if (!contact.id || !contact.name) return;

  await deleteContactTypeAsync({
    id: Number(contact.id),
  });

  actionDelete();
  contact.id = 0;
  contact.name = "";
  await queryClient.invalidateQueries(["contact-types"]);
};
</script>

<template>
  <BreadCrumb :pages="pages" />

  <div class="mt-6 flex min-h-full flex-col gap-6 xl:flex-row">
    <ShadowBox class="bg-white w-full md:w-1/2 mx-auto">
      <div class="flex justify-between items-center mb-2">
        <h1 class="text-2xl font-bold">Tipo de contactos</h1>
        <StyledButton
          class="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded"
          @click="actionCreate"
        >
          <span class="material-symbols-outlined"> add </span>
          Crear tipo contacto
        </StyledButton>
      </div>

      <ProviderList
        :data="
          contactTypes?.map((contactType) => ({
            ...contactType,
            id: contactType.id,
            name: contactType.name,
            count: 0,
            createdAt: contactType.createdAt,
          })) || []
        "
        model=""
        name-permission-delete=""
        name-permission-edit=""
        name-permission-view=""
      >
        <template #delete="{ entry }">
          <StyledButton
            @click="
              () => {
                contact.id = entry.id;
                contact.name = entry.name;
                actionDelete();
              }
            "
            class="text-red-500 hover:bg-white"
          >
            <span class="material-symbols-outlined"> delete </span>
          </StyledButton>
        </template>
      </ProviderList>
    </ShadowBox>
  </div>

  <!-- Modal create contact -->
  <ModalComponent
    :model-value="actionModalCreate"
    @update:model-value="
      () => {
        actionCreate();
      }
    "
  >
    <template #modalBody>
      <div class="p-5">
        <form @submit.prevent="handleCreate">
          <StyledInput
            v-model="contact.name"
            label="Nombre del tipo de contacto"
            placeholder="Nombre del  tipo de contacto"
            class="flex-1 mb-3"
          />

          <StyledButton
            class="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded ml-auto"
            :disabled="createContactTypeLoading"
            :loading="createContactTypeLoading"
          >
            Crear tipo contacto
          </StyledButton>
        </form>
      </div>
    </template>
  </ModalComponent>

  <!-- Alerte delete contact -->
  <AlertComponent
    :model-value="actionModalDelete"
    type="danger"
    :dismiss="true"
    :on-confirm="() => handleDelete()"
    :on-cancel="actionDelete"
  >
    <template #title>
      <h3>Eliminar tpo contacto: ({{ contact.name }})</h3>
    </template>

    <template #body>
      <p class="font-normal text-sm">
        Al eliminar este tipo contacto, se eliminarán todos los datos asociados
        a él.
      </p>
    </template>

    <template #closeButton> Cancelar </template>

    <template #button> Eliminar </template>
  </AlertComponent>

  <LoadingModal :when="isLoading" />
</template>
